import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface CashMovement {
  id: string;
  type: "opening" | "sale" | "refund" | "expense" | "deposit" | "withdrawal" | "closing";
  amount: number;
  description: string;
  time: string;
  user: string;
}

interface RegisterStatus {
  isOpen: boolean;
  openedAt?: string;
  openedBy?: string;
  openingAmount: number;
  currentCash: number;
  expectedCash: number;
  totalSales: number;
  totalRefunds: number;
  totalTransactions: number;
}

const MOCK_MOVEMENTS: CashMovement[] = [
  { id: "1", type: "opening", amount: 500.00, description: "Apertura de caja", time: "08:00", user: "Juan" },
  { id: "2", type: "sale", amount: 156.50, description: "Venta #001234", time: "09:15", user: "Juan" },
  { id: "3", type: "sale", amount: 89.99, description: "Venta #001235", time: "10:30", user: "Juan" },
  { id: "4", type: "expense", amount: -50.00, description: "Compra de suministros", time: "11:00", user: "Juan" },
  { id: "5", type: "sale", amount: 245.00, description: "Venta #001236", time: "12:45", user: "Juan" },
  { id: "6", type: "refund", amount: -35.75, description: "Reembolso #001237", time: "13:20", user: "Juan" },
  { id: "7", type: "deposit", amount: 200.00, description: "Depósito adicional", time: "14:00", user: "Juan" },
];

const MOCK_STATUS: RegisterStatus = {
  isOpen: true,
  openedAt: "08:00",
  openedBy: "Juan Pérez",
  openingAmount: 500.00,
  currentCash: 1105.74,
  expectedCash: 1105.74,
  totalSales: 491.49,
  totalRefunds: 35.75,
  totalTransactions: 4,
};

export default function RegisterSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState<RegisterStatus>(MOCK_STATUS);
  const [movements, setMovements] = useState<CashMovement[]>(MOCK_MOVEMENTS);
  const [showAddMovement, setShowAddMovement] = useState(false);
  const [showCloseRegister, setShowCloseRegister] = useState(false);
  const [newMovement, setNewMovement] = useState({ type: "deposit", amount: "", description: "" });
  const [countedCash, setCountedCash] = useState("");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
  };

  const getMovementIcon = (type: CashMovement["type"]) => {
    switch (type) {
      case "opening":
        return { icon: "ri-door-open-line", color: "text-blue-500", bg: "bg-blue-500/10" };
      case "sale":
        return { icon: "ri-shopping-bag-line", color: "text-primary-500", bg: "bg-primary-500/10" };
      case "refund":
        return { icon: "ri-refund-2-line", color: "text-orange-500", bg: "bg-orange-500/10" };
      case "expense":
        return { icon: "ri-arrow-up-circle-line", color: "text-red-500", bg: "bg-red-500/10" };
      case "deposit":
        return { icon: "ri-arrow-down-circle-line", color: "text-emerald-500", bg: "bg-emerald-500/10" };
      case "withdrawal":
        return { icon: "ri-arrow-up-circle-line", color: "text-purple-500", bg: "bg-purple-500/10" };
      case "closing":
        return { icon: "ri-door-closed-line", color: "text-gray-500", bg: "bg-gray-500/10" };
      default:
        return { icon: "ri-money-dollar-circle-line", color: "text-gray-500", bg: "bg-gray-500/10" };
    }
  };

  const handleAddMovement = () => {
    const amount = parseFloat(newMovement.amount);
    if (amount && newMovement.description) {
      const movement: CashMovement = {
        id: Date.now().toString(),
        type: newMovement.type as CashMovement["type"],
        amount: newMovement.type === "withdrawal" || newMovement.type === "expense" ? -amount : amount,
        description: newMovement.description,
        time: new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }),
        user: "Usuario Actual",
      };
      setMovements((prev) => [...prev, movement]);
      setStatus((prev) => ({
        ...prev,
        currentCash: prev.currentCash + movement.amount,
        expectedCash: prev.expectedCash + movement.amount,
      }));
      setNewMovement({ type: "deposit", amount: "", description: "" });
      setShowAddMovement(false);
    }
  };

  const handleCloseRegister = () => {
    const counted = parseFloat(countedCash) || 0;
    const difference = counted - status.expectedCash;
    alert(`Caja cerrada. Diferencia: $${difference.toFixed(2)}`);
    setShowCloseRegister(false);
  };

  const difference = status.currentCash - status.expectedCash;

  return (
    <div className="h-full flex flex-col">
      <div className={`p-4 sm:p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              <i className="ri-safe-2-line mr-2 text-primary-500"></i>
              {t("posRegister.title")}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm ${
                status.isOpen ? "bg-primary-500/10 text-primary-500" : "bg-red-500/10 text-red-500"
              }`}>
                <span className={`w-2 h-2 rounded-full ${status.isOpen ? "bg-primary-500" : "bg-red-500"} mr-2 animate-pulse`}></span>
                {status.isOpen ? t("posRegister.open") : t("posRegister.closed")}
              </span>
              {status.isOpen && status.openedAt && (
                <span className={`text-sm ${themeClasses.textSecondary}`}>
                  {t("posRegister.openedAt")} {status.openedAt} {t("posRegister.by")} {status.openedBy}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {status.isOpen ? (
              <>
                <button
                  onClick={() => setShowAddMovement(true)}
                  className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium`}
                >
                  <i className="ri-add-line mr-2"></i>
                  {t("posRegister.addMovement")}
                </button>
                <button
                  onClick={() => setShowCloseRegister(true)}
                  className="px-4 py-2 rounded-xl bg-red-500 text-white font-medium"
                >
                  <i className="ri-door-closed-line mr-2"></i>
                  {t("posRegister.closeRegister")}
                </button>
              </>
            ) : (
              <button className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium">
                <i className="ri-door-open-line mr-2"></i>
                {t("posRegister.openRegister")}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <i className="ri-wallet-3-line text-xl text-blue-500"></i>
              </div>
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posRegister.openingAmount")}</p>
            </div>
            <p className={`text-2xl font-bold ${themeClasses.text}`}>${status.openingAmount.toFixed(2)}</p>
          </div>

          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-xl text-primary-500"></i>
              </div>
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posRegister.totalSales")}</p>
            </div>
            <p className="text-2xl font-bold text-primary-500">${status.totalSales.toFixed(2)}</p>
            <p className={`text-sm ${themeClasses.textSecondary}`}>{status.totalTransactions} {t("posRegister.transactions")}</p>
          </div>

          <div className={`${themeClasses.cardBg} rounded-xl p-4 border ${themeClasses.border}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <i className="ri-refund-2-line text-xl text-orange-500"></i>
              </div>
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posRegister.totalRefunds")}</p>
            </div>
            <p className="text-2xl font-bold text-orange-500">-${status.totalRefunds.toFixed(2)}</p>
          </div>

          <div className={`${themeClasses.cardBg} rounded-xl p-4 border-2 border-primary-500`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-xl text-primary-500"></i>
              </div>
              <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posRegister.currentCash")}</p>
            </div>
            <p className="text-2xl font-bold text-primary-500">${status.currentCash.toFixed(2)}</p>
            {difference !== 0 && (
              <p className={`text-sm ${difference > 0 ? "text-primary-500" : "text-red-500"}`}>
                {difference > 0 ? "+" : ""}{difference.toFixed(2)} {t("posRegister.difference")}
              </p>
            )}
          </div>
        </div>

        <div className={`${themeClasses.cardBg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
          <div className={`p-4 border-b ${themeClasses.border}`}>
            <h2 className={`font-bold ${themeClasses.text}`}>{t("posRegister.movements")}</h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {movements.map((movement) => {
              const style = getMovementIcon(movement.type);
              return (
                <div key={movement.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${style.bg} flex items-center justify-center`}>
                      <i className={`${style.icon} text-xl ${style.color}`}></i>
                    </div>
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>{movement.description}</p>
                      <p className={`text-sm ${themeClasses.textSecondary}`}>
                        {movement.time} • {movement.user}
                      </p>
                    </div>
                  </div>
                  <span className={`font-bold text-lg ${movement.amount >= 0 ? "text-primary-500" : "text-red-500"}`}>
                    {movement.amount >= 0 ? "+" : ""}${Math.abs(movement.amount).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showAddMovement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.bg} rounded-2xl p-6 w-full max-w-md border ${themeClasses.border}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>{t("posRegister.addMovement")}</h2>
              <button onClick={() => setShowAddMovement(false)} className={themeClasses.textSecondary}>
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posRegister.movementType")}
                </label>
                <select
                  value={newMovement.type}
                  onChange={(e) => setNewMovement((prev) => ({ ...prev, type: e.target.value }))}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                >
                  <option value="deposit">{t("posRegister.deposit")}</option>
                  <option value="withdrawal">{t("posRegister.withdrawal")}</option>
                  <option value="expense">{t("posRegister.expense")}</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posRegister.amount")}
                </label>
                <input
                  type="number"
                  value={newMovement.amount}
                  onChange={(e) => setNewMovement((prev) => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posRegister.description")}
                </label>
                <input
                  type="text"
                  value={newMovement.description}
                  onChange={(e) => setNewMovement((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t("posRegister.descriptionPlaceholder")}
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              <button
                onClick={handleAddMovement}
                className="w-full p-4 rounded-xl bg-primary-500 text-gray-900 font-bold"
              >
                {t("posRegister.addMovement")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCloseRegister && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.bg} rounded-2xl p-6 w-full max-w-md border ${themeClasses.border}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>{t("posRegister.closeRegister")}</h2>
              <button onClick={() => setShowCloseRegister(false)} className={themeClasses.textSecondary}>
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${themeClasses.cardBg} border ${themeClasses.border}`}>
                <div className="flex justify-between mb-2">
                  <span className={themeClasses.textSecondary}>{t("posRegister.expectedCash")}</span>
                  <span className={`font-bold ${themeClasses.text}`}>${status.expectedCash.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  {t("posRegister.countedCash")}
                </label>
                <input
                  type="number"
                  value={countedCash}
                  onChange={(e) => setCountedCash(e.target.value)}
                  placeholder="0.00"
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500 text-2xl text-center`}
                />
              </div>

              {countedCash && (
                <div className={`p-4 rounded-xl ${
                  parseFloat(countedCash) === status.expectedCash
                    ? "bg-primary-500/10 border border-primary-500/30"
                    : "bg-orange-500/10 border border-orange-500/30"
                }`}>
                  <div className="flex justify-between">
                    <span className={parseFloat(countedCash) === status.expectedCash ? "text-primary-500" : "text-orange-500"}>
                      {t("posRegister.difference")}
                    </span>
                    <span className={`font-bold ${parseFloat(countedCash) === status.expectedCash ? "text-primary-500" : "text-orange-500"}`}>
                      ${((parseFloat(countedCash) || 0) - status.expectedCash).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleCloseRegister}
                disabled={!countedCash}
                className="w-full p-4 rounded-xl bg-red-500 text-white font-bold disabled:opacity-50"
              >
                {t("posRegister.confirmClose")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
